/**
 * Dashboard - Exact replica of DashboardBlock with HTML-like API
 * Preserves all original styling and functionality
 */

import React from 'react'
import { Box, CardMedia, Container, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { CheckCircleOutlined } from '@ant-design/icons'
import TextBlock from '../blocks/TextBlock'

interface DashboardProps {
	title: string
	content: string
	image: string
	imageAlt?: string
	features?: string[]
}

const Dashboard: React.FC<DashboardProps> = ({
	title,
	content,
	image,
	imageAlt = 'Dashboard interface',
	features = [
		'See overall test coverage and failure rate',
		'See what pages need attention most',
		'Zoom in on functionality or accessibility issues'
	]
}) => {
	const theme = useTheme()

	const checkIcon = <CheckCircleOutlined style={{ color: theme.palette.primary.main, fontSize: '1.15rem' }} />

	return (
		<Box
			component="section"
			sx={{
				overflow: 'hidden',
				position: 'relative',
				'&:after': {
					content: '""',
					position: 'absolute',
					width: '100%',
					height: '40%',
					bottom: 0,
					left: 0,
					//@ts-expect-error
					background: theme.palette.secondary[800],
					[theme.breakpoints.down('sm')]: { height: '60%' }
				},
				py: { md: 10, xs: 2.5 }, // Exact padding from original
				backgroundColor: theme.palette.mode === 'dark' ? theme.palette.custom.main : theme.palette.grey[50]
			}}
		>
			<Container
				sx={{
					position: 'relative',
					zIndex: 1,
					textAlign: 'center'
				}}
			>
				<TextBlock title={title} titleLevel={2} content={content} align="center" spacing="normal" maxWidth="600px" />

				<CardMedia
					sx={{ width: '75%', maxWidth: 910, mx: 'auto', mb: 3 }} // Exact styling from original
					component="img"
					image={image}
					alt={imageAlt}
					height={570} // Exact height from original
					style={{ objectFit: 'cover' }}
				/>

				<Box
					sx={{
						maxWidth: '75%',
						mx: 'auto',
						display: 'flex',
						justifyContent: 'space-between',
						flexDirection: { md: 'row', xs: 'column' }
					}}
				>
					{features.map(_content => (
						<Box
							key={_content}
							sx={{
								display: 'flex',
								alignItems: 'start',
								mb: 2,
								maxWidth: { md: '33%', xs: '100%' },
								textAlign: 'start'
							}}
						>
							{checkIcon}
							<Typography variant="body1" color="secondary" sx={{ ml: 1, mb: 2 }}>
								{_content}
							</Typography>
						</Box>
					))}
				</Box>
			</Container>
		</Box>
	)
}

export default Dashboard
